import React from 'react';
import { compose } from 'redux';
import classnames from 'classnames';
import { withTranslation, WithTranslation } from 'react-i18next';

import Button from '@wix/da-shared-react/pkg/Button';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import IconTrashcan from '@wix/da-ds/pkg/Icons/24x24/Trashcan';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';

import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import {
  withMobileContext,
  WithMobileContextProps,
} from '@wix/da-react-context/pkg/MobileContext';

import withConfirmationModal, {
  WithConfirmationModalProps,
} from '../../_hoc/withConfirmationModal';
import reduxConnectContainer, {
  ConnectedKeys,
} from './WidgetBoxWide.container';
import WidgetBoxWrapper from '../WidgetBoxWrapper';
import WidgetHeaderWide from '../WidgetHeaderWide';
import WidgetHeaderMobile from '../WidgetHeaderMobile';
import WidgetEditControls from '../WidgetEditControls';
import { ModuleType } from '../../../../../types/modules';
import { ZoneType } from '../../../../../types/layout';
import { UpdateModulePayload } from '../../../../actions/modules';
import { moduleTypeToTranslationKey } from '../../../../../utils/modules';

import s from './WidgetBoxWide.scss';

export interface Props {
  section: string;
  zone: ZoneType;
  moduleId: number;
  moduleType: ModuleType;
  moduleVersion: number;
  id: string;
  header: string | React.ReactNode;
  count?: number | string;
  subHeader?: string;
  autoShowWidgetConfig?: boolean;
  headerExtraElementsOnMobile?: React.ReactNode;
  profileOwnerId?: number;
  isAuthorizedToEditWidget?: boolean;
  isAuthorizedToRemoveWidget?: boolean;
  isHeaderStickyOnMobile?: boolean;
  isCurrentUserProfileOwner?: boolean;
  isAuthorizedToMoveModule?: boolean;
  isNewlyAddedModule?: boolean;
  hasMore?: boolean;
  fetchMore?: () => void;
  isFetching?: boolean;
  isEmpty?: boolean;
  emptyView?: React.ReactNode;
  className?: string;
  widgetBoxClassName?: string;
  children: React.ReactNode;
  widgetMenuRenderer?: (closeMenu) => React.ReactNode;
  widgetMoveUp?: () => void;
  widgetMoveDown?: () => void;
  widgetRemove?: () => void;
  editControlsTooltipTitle?: string;
  editControlsTooltipContent?: React.ReactNode;
  updateModule?: (payload: UpdateModulePayload) => void;
  parentRef: any;
  biggerHeader?: boolean;
}

export interface State {
  isWidgetConfigShown: boolean;
}

const enhance = compose(
  reduxConnectContainer,
  withConfirmationModal,
  withMobileContext,
  withTranslation()
);
class WidgetBoxWide extends React.PureComponent<
  Props & WithTranslation & WithConfirmationModalProps & WithMobileContextProps,
  State
> {
  state = {
    isWidgetConfigShown: false,
  };

  render() {
    const {
      moduleId,
      isMobile,
      isEmpty,
      emptyView,
      children,
      moduleType,
      id,
      className,
      parentRef,
      header,
      subHeader,
      count,
      isHeaderStickyOnMobile,
      headerExtraElementsOnMobile,
      isAuthorizedToEditWidget,
      isAuthorizedToRemoveWidget,
      isCurrentUserProfileOwner,
      isAuthorizedToMoveModule,
      hasMore,
      isFetching,
      fetchMore,
      profileOwnerId,
      biggerHeader = false,
    } = this.props;

    if (isEmpty && !emptyView) {
      return null;
    }
    const canUserMoveWidget =
      isCurrentUserProfileOwner || isAuthorizedToMoveModule;
    const isAuthorized =
      canUserMoveWidget ||
      isAuthorizedToRemoveWidget ||
      isAuthorizedToEditWidget;

    return (
      <BiLoggerContextProvider
        value={{ widgetname: moduleType, sectionname: moduleType }}
      >
        <WidgetBoxWrapper
          id={id}
          className={classnames(s['widget-wrapper'], className)}
          ref={parentRef}
          data-moduleid={moduleId}
          data-userid={profileOwnerId}
        >
          <ErrorBoundary>
            {isMobile ? (
              <WidgetHeaderMobile
                header={header}
                count={count}
                isSticky={isHeaderStickyOnMobile}
                extras={headerExtraElementsOnMobile}
                isWide
              />
            ) : (
              <WidgetHeaderWide
                header={header}
                subHeader={subHeader}
                extras={isAuthorized && this.renderEditControls()}
                bigger={biggerHeader}
              />
            )}
            <>{isEmpty ? emptyView : children}</>
            {!isEmpty && hasMore && (
              <Button
                view="b8"
                disabled={isFetching}
                className={s['view-more-button']}
                onClick={fetchMore}
                biData={{
                  evid: BiEventType.PROFILE_LOAD_MORE_CLICK,
                  typeid: ResourceType.USER,
                  itemid: profileOwnerId,
                }}
              >
                {this.props.t!('common.loadMore')}
              </Button>
            )}
          </ErrorBoundary>
        </WidgetBoxWrapper>
      </BiLoggerContextProvider>
    );
  }

  renderEditControls() {
    return (
      <div className={s['edit-widget-controls-wrapper']}>
        <WidgetEditControls
          onMoveUp={this.props.widgetMoveUp}
          onMoveDown={this.props.widgetMoveDown}
          onRemove={
            this.props.isAuthorizedToRemoveWidget
              ? this.handleWidgetRemove
              : undefined
          }
          onEdit={
            this.props.widgetMenuRenderer &&
            !!this.props.isAuthorizedToEditWidget
              ? this.showWidgetConfig
              : undefined
          }
          tooltipTitle={this.props.editControlsTooltipTitle}
          tooltipContent={this.props.editControlsTooltipContent}
        />
        {this.renderWidgetConfig()}
      </div>
    );
  }

  renderWidgetConfig() {
    const { widgetMenuRenderer, autoShowWidgetConfig, isNewlyAddedModule } =
      this.props;
    const { isWidgetConfigShown } = this.state;

    if (!widgetMenuRenderer) {
      return null;
    }

    if (isWidgetConfigShown || (isNewlyAddedModule && autoShowWidgetConfig)) {
      return widgetMenuRenderer && widgetMenuRenderer(this.hideWidgetConfig);
    }
  }

  showWidgetConfig = () => {
    this.setState({ isWidgetConfigShown: true });
  };

  hideWidgetConfig = () => {
    const { isNewlyAddedModule, updateModule } = this.props;
    this.setState({ isWidgetConfigShown: false });
    isNewlyAddedModule &&
      updateModule &&
      updateModule({
        id: this.props.moduleId,
        moduleMeta: {
          isNewlyAdded: false,
        },
      });
  };

  handleWidgetRemove = () => {
    const { moduleType, widgetRemove, pushConfirmationModal, t } = this.props;
    const sectionName = t(moduleTypeToTranslationKey(moduleType));
    pushConfirmationModal({
      title: t('widgets.common.removeWidget.header', {
        sectionName,
      }),
      text: t('widgets.common.removeWidget.body'),
      confirmBtnLabel: t('widgets.common.removeWidget.confirmBtnLabel'),
      cancelBtnLabel: t('widgets.common.removeWidget.cancelBtnLabel'),
      icon: <IconTrashcan size={IconSize.MEGA} />,
      onConfirm: widgetRemove,
    });
  };
}

export default enhance(WidgetBoxWide) as React.ComponentType<
  Omit<Props, ConnectedKeys>
>;
